/**
 * Deposit enums - SOURCE OF TRUTH
 * Pure TypeScript - no Drizzle imports
 */
// Deposit status labels.
//
// `forfeited` is APPENDED, not slotted into lifecycle order:
// `depositStatusValues` is `Object.keys(...)`, so position determines the
// Postgres enum's value order and inserting mid-record would rewrite it.
// The ENG-1306 link states (`revoked`, `failed`, `needs_review`) follow the
// same rule.
export const depositStatusLabels = {
    pending: 'Pending',
    paid: 'Paid',
    /** The link lapsed before it was paid — the deposit is still owed. */
    expired: 'Unpaid',
    refunded: 'Refunded',
    cancelled: 'Cancelled',
    forfeited: 'Forfeited',
    /** Staff/Claire withdrew the link; its Stripe session was expired. */
    revoked: 'Revoked',
    /**
     * The Stripe session could not be created; no usable link exists. Worded
     * as the link, not the deposit: the patient was never asked to pay, so
     * "Deposit failed" read as a failed payment.
     */
    failed: 'Link not created',
    /**
     * Money was captured but the booking could NOT be confirmed (hold lapsed,
     * appointment cancelled, or slot taken). Not booked, not auto-refunded —
     * staff decide.
     */
    needs_review: 'Needs review',
};
export const depositStatusValues = Object.keys(depositStatusLabels);
/** Who issued a deposit payment link (ENG-1306). */
export const depositLinkSourceLabels = {
    staff: 'Staff',
    claire: 'Claire',
    public: 'Online booking',
};
export const depositLinkSourceValues = Object.keys(depositLinkSourceLabels);
/**
 * What a customer pays WHEN they book, per service.
 *
 * Replaces the `requiresDeposit` boolean, which could express only two of these
 * three states — `full` had no representation at all. See
 * docs/plans/service-pricing-model.md §6 and
 * docs/plans/payment-policy-and-deposits.md.
 */
export const servicePaymentPolicyLabels = {
    /** Nothing online; the practitioner rings it up at the POS after the visit. */
    in_clinic: 'Pay at the clinic',
    /** Part now, remainder at the POS. */
    deposit: 'Deposit at booking',
    /** The whole price up front. Only offerable where the exact price resolves. */
    full: 'Pay in full at booking',
};
export const servicePaymentPolicyValues = Object.keys(servicePaymentPolicyLabels);
/**
 * How a `deposit` amount is arrived at.
 *
 * `percent` is only meaningful where an exact price exists — a percentage of a
 * `from` floor under-charges every time, and `poa` has no base at all — so the
 * service form offers `fixed` for those.
 */
export const depositBasisLabels = {
    fixed: 'Fixed amount',
    percent: 'Percentage of price',
};
export const depositBasisValues = Object.keys(depositBasisLabels);
/**
 * How several deposit-bearing services on ONE appointment combine.
 *
 * A deposit protects against a no-show, and a no-show is per APPOINTMENT — the
 * customer either turns up or doesn't — so `largest` is the honest default.
 * `sum` (three deposits for one visit) is preserved for orgs already collecting
 * that way, because changing it silently would change their takings.
 */
export const depositAggregationLabels = {
    largest: 'Highest single deposit',
    sum: 'Total of every deposit',
};
export const depositAggregationValues = Object.keys(depositAggregationLabels);
//# sourceMappingURL=deposits.js.map